import { CheckIcon } from 'lucide-react'
import type { components } from '@joyful/openapi/types.gen'
import { cn, formatSnakeCaseLabel } from '@joyful/utils'
import {
  Box,
  DelayedSpinner,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  QueriesState,
  toast,
} from '../../../../core'
import { useUserDropdownMenu } from '../../../components/UserDropdownMenuContext'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import { useCurrentUser } from '../../../utils/currentUserContext'
import {
  useApiMutation,
  useApiQuery,
  useApiQueryClient,
} from '../../../utils/reactQuery'

type Account = components['schemas']['AccountWithSlugRead']
type FeatureFlagSettings = components['schemas']['FeatureFlagSettingsRead']

function isFeatureFlagFullyLaunched({
  accounts,
  flag,
}: {
  accounts: ReadonlyArray<Account>
  flag: FeatureFlagSettings
}) {
  const targetedAccountIds = new Set(flag.targeted_account_ids ?? [])
  const activeAccounts = accounts.filter((account) => {
    return account.is_active
  })
  if (flag.status !== 'ON' || activeAccounts.length === 0) {
    return false
  }

  return activeAccounts.every((account) => {
    const isTargeted = targetedAccountIds.has(account.id)
    return isTargeted !== flag.default_value
  })
}

export function OverrideFeatureFlagDropdownMenuItem() {
  const canManageFeatureFlags = useHasUserCapability(
    'admin.feature_flags.manage',
  )
  const userDropdownMenu = useUserDropdownMenu()

  if (!canManageFeatureFlags) {
    return null
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{`Override feature flag`}</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="max-h-96">
          {userDropdownMenu.state.isUserDropdownMenuOpen ? (
            <OverrideFeatureFlagMenuContent />
          ) : null}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

function OverrideFeatureFlagMenuContent() {
  const currentUser = useCurrentUser()
  const { invalidateApiQueries } = useApiQueryClient()
  const featureFlagsQuery = useApiQuery('get', '/feature-flags')
  const accountsQuery = useApiQuery('get', '/accounts')
  const replaceUserOverrides = useApiMutation(
    'put',
    '/feature-flags/{feature_flag_id}/users',
    {
      onSuccess: async () => {
        await invalidateApiQueries('/feature-flags', '/users/me')
      },
    },
  )

  const testableFlags =
    featureFlagsQuery.isSuccess && accountsQuery.isSuccess
      ? featureFlagsQuery.data.active.filter((flag) => {
          if (flag.status !== 'ON') {
            return false
          }

          return !isFeatureFlagFullyLaunched({
            accounts: accountsQuery.data,
            flag,
          })
        })
      : []

  return (
    <QueriesState
      queries={[featureFlagsQuery, accountsQuery]}
      loading={
        <Box padding="sm">
          <DelayedSpinner size="sm" />
        </Box>
      }
      error={() => {
        return (
          <DropdownMenuItem disabled>
            {`Failed to load feature flags`}
          </DropdownMenuItem>
        )
      }}
    >
      {testableFlags.length === 0 ? (
        <DropdownMenuItem
          disabled
        >{`No flags are rolling out`}</DropdownMenuItem>
      ) : (
        testableFlags.map((flag) => {
          const passUserIds = flag.pass_user_ids ?? []
          const isOverridden = passUserIds.includes(currentUser.id)
          const flagTitle = formatSnakeCaseLabel(flag.flag)
          const persistOverride = (isChecked: boolean) => {
            const nextPassUserIds = new Set(passUserIds)
            if (isChecked) {
              nextPassUserIds.add(currentUser.id)
            } else {
              nextPassUserIds.delete(currentUser.id)
            }

            const persistPromise = replaceUserOverrides.mutateAsync({
              body: {
                pass_user_ids: [...nextPassUserIds],
              },
              params: {
                path: {
                  feature_flag_id: flag.id,
                },
              },
            })
            toast.promise(persistPromise, {
              error: `Failed to update ${flagTitle}`,
              loading: isChecked
                ? `Adding you as an override for ${flagTitle}…`
                : `Removing you as an override for ${flagTitle}…`,
              success: isChecked
                ? `Added you as an override for ${flagTitle}.`
                : `Removed you as an override for ${flagTitle}.`,
            })
          }
          return (
            <DropdownMenuCheckboxItem
              key={flag.id}
              checked={isOverridden}
              disabled={replaceUserOverrides.isPending}
              subtitle={flag.description}
              endAdornment={
                <CheckIcon
                  className={cn(
                    'size-4 shrink-0 stroke-2',
                    isOverridden ? 'opacity-100' : 'opacity-0',
                  )}
                />
              }
              onCheckedChange={persistOverride}
            >
              {flagTitle}
            </DropdownMenuCheckboxItem>
          )
        })
      )}
    </QueriesState>
  )
}
